/**
 * ESC/POS Command Builder
 *
 * Generates raw ESC/POS commands for thermal receipt printers.
 * Reference: https://reference.epson-biz.com/modules/ref_escpos/index.php
 */

import type { ReceiptData } from './types'

// ESC/POS Constants
const ESC = 0x1b
const GS = 0x1d
const LF = 0x0a

// Command sequences
const COMMANDS = {
  // Initialize printer
  INIT: new Uint8Array([ESC, 0x40]),

  // Text formatting
  ALIGN_LEFT: new Uint8Array([ESC, 0x61, 0x00]),
  ALIGN_CENTER: new Uint8Array([ESC, 0x61, 0x01]),
  ALIGN_RIGHT: new Uint8Array([ESC, 0x61, 0x02]),

  // Font size
  FONT_NORMAL: new Uint8Array([ESC, 0x21, 0x00]),
  FONT_DOUBLE_HEIGHT: new Uint8Array([ESC, 0x21, 0x10]),
  FONT_DOUBLE_WIDTH: new Uint8Array([ESC, 0x21, 0x20]),
  FONT_DOUBLE: new Uint8Array([ESC, 0x21, 0x30]), // Double height + width

  // Emphasis
  BOLD_ON: new Uint8Array([ESC, 0x45, 0x01]),
  BOLD_OFF: new Uint8Array([ESC, 0x45, 0x00]),

  // Line spacing
  LINE_SPACING_DEFAULT: new Uint8Array([ESC, 0x32]),
  LINE_SPACING_SET: (n: number) => new Uint8Array([ESC, 0x33, n]),

  // Paper cut
  CUT_PARTIAL: new Uint8Array([GS, 0x56, 0x01]),
  CUT_FULL: new Uint8Array([GS, 0x56, 0x00]),

  // Feed
  FEED_LINES: (n: number) => new Uint8Array([ESC, 0x64, n]),
  NEW_LINE: new Uint8Array([LF]),
}

// Receipt paper width (characters) - standard 58mm thermal paper
const PAPER_WIDTH = 32

/**
 * Text encoder for converting strings to bytes
 */
const textEncoder = new TextEncoder()

/**
 * Encode text to Uint8Array
 */
function encodeText(text: string): Uint8Array {
  return textEncoder.encode(text)
}

/**
 * Concatenate multiple Uint8Arrays
 */
function concat(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const arr of arrays) {
    result.set(arr, offset)
    offset += arr.length
  }
  return result
}

/**
 * Create a line of dashes
 */
function dashLine(): Uint8Array {
  return encodeText('-'.repeat(PAPER_WIDTH))
}

/**
 * Create a line of equals signs
 */
function doubleLine(): Uint8Array {
  return encodeText('='.repeat(PAPER_WIDTH))
}

/**
 * Pad text to fill width, with text on left and right
 */
function padBetween(left: string, right: string, width: number = PAPER_WIDTH): string {
  const padding = width - left.length - right.length
  if (padding < 1) {
    // Truncate left side if too long
    const truncated = left.substring(0, width - right.length - 1)
    return `${truncated} ${right}`
  }
  return left + ' '.repeat(padding) + right
}

/**
 * Truncate text to max length
 */
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

/**
 * Format time for receipt
 */
function formatTime(date: Date): string {
  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

/**
 * Build receipt data for an order
 */
export function buildReceipt(data: ReceiptData): Uint8Array {
  const parts: Uint8Array[] = []

  // Initialize printer
  parts.push(COMMANDS.INIT)

  // Header - Event/Vendor name (if available)
  if (data.eventName || data.vendorName) {
    parts.push(COMMANDS.ALIGN_CENTER)
    parts.push(COMMANDS.FONT_NORMAL)
    if (data.eventName) {
      parts.push(encodeText(truncate(data.eventName, PAPER_WIDTH)))
      parts.push(COMMANDS.NEW_LINE)
    }
    if (data.vendorName) {
      parts.push(encodeText(truncate(data.vendorName, PAPER_WIDTH)))
      parts.push(COMMANDS.NEW_LINE)
    }
    parts.push(COMMANDS.NEW_LINE)
  }

  // Order number (large)
  parts.push(COMMANDS.ALIGN_CENTER)
  parts.push(COMMANDS.FONT_DOUBLE)
  parts.push(COMMANDS.BOLD_ON)
  parts.push(encodeText(`#${data.orderCode}`))
  parts.push(COMMANDS.NEW_LINE)
  parts.push(COMMANDS.BOLD_OFF)
  parts.push(COMMANDS.FONT_NORMAL)

  // Separator
  parts.push(dashLine())
  parts.push(COMMANDS.NEW_LINE)

  // Time and customer info
  parts.push(COMMANDS.ALIGN_LEFT)
  const timeStr = formatTime(data.orderTime)
  const customerStr = data.customerName || 'Guest'
  parts.push(encodeText(padBetween(`Time: ${timeStr}`, '')))
  parts.push(COMMANDS.NEW_LINE)
  parts.push(encodeText(padBetween(`Name: ${truncate(customerStr, 20)}`, '')))
  parts.push(COMMANDS.NEW_LINE)

  // Separator
  parts.push(doubleLine())
  parts.push(COMMANDS.NEW_LINE)
  parts.push(COMMANDS.NEW_LINE)

  // Items header
  parts.push(COMMANDS.BOLD_ON)
  parts.push(encodeText(padBetween('QTY  ITEM', 'TOKENS')))
  parts.push(COMMANDS.NEW_LINE)
  parts.push(COMMANDS.BOLD_OFF)
  parts.push(dashLine())
  parts.push(COMMANDS.NEW_LINE)

  // Items
  for (const item of data.items) {
    const qtyStr = item.qty.toString().padStart(2, ' ')
    const itemName = truncate(item.name, PAPER_WIDTH - 12)
    const priceStr = item.price !== undefined ? item.price.toString() : ''

    // First line: qty and item name
    parts.push(encodeText(`${qtyStr}x  ${itemName}`))
    parts.push(COMMANDS.NEW_LINE)

    // Price on right if available
    if (priceStr) {
      parts.push(encodeText(padBetween('', priceStr)))
      parts.push(COMMANDS.NEW_LINE)
    }
  }

  // Separator
  parts.push(dashLine())
  parts.push(COMMANDS.NEW_LINE)

  // Totals
  parts.push(COMMANDS.FONT_DOUBLE_HEIGHT)
  parts.push(COMMANDS.BOLD_ON)
  parts.push(encodeText(padBetween('TOTAL:', `${data.totalItems} Items`)))
  parts.push(COMMANDS.NEW_LINE)
  parts.push(encodeText(padBetween('', `${data.totalTokens} Tokens`)))
  parts.push(COMMANDS.NEW_LINE)
  parts.push(COMMANDS.BOLD_OFF)
  parts.push(COMMANDS.FONT_NORMAL)

  // Notes (if any)
  if (data.note && data.note.trim()) {
    parts.push(COMMANDS.NEW_LINE)
    parts.push(doubleLine())
    parts.push(COMMANDS.NEW_LINE)
    parts.push(encodeText('*** NOTES ***'))
    parts.push(COMMANDS.NEW_LINE)

    // Word wrap notes
    const words = data.note.split(' ')
    let currentLine = ''
    for (const word of words) {
      if (currentLine.length + word.length + 1 > PAPER_WIDTH) {
        parts.push(encodeText(currentLine))
        parts.push(COMMANDS.NEW_LINE)
        currentLine = word
      } else {
        currentLine = currentLine ? `${currentLine} ${word}` : word
      }
    }
    if (currentLine) {
      parts.push(encodeText(currentLine))
      parts.push(COMMANDS.NEW_LINE)
    }
  }

  // Footer spacing and cut
  parts.push(COMMANDS.FEED_LINES(4))
  parts.push(COMMANDS.CUT_PARTIAL)

  return concat(...parts)
}

/**
 * Build a simple test receipt
 */
export function buildTestReceipt(): Uint8Array {
  return buildReceipt({
    orderCode: 'TEST-001',
    orderTime: new Date(),
    customerName: 'Test Customer',
    items: [
      { name: 'Test Item 1', qty: 2, price: 5 },
      { name: 'Test Item 2', qty: 1, price: 3 },
    ],
    totalItems: 3,
    totalTokens: 13,
    note: 'This is a test print',
    vendorName: 'Test Vendor',
    eventName: 'Test Event',
  })
}

type QRReceiptData = {
  eventName?: string
  userName?: string
  barcode: string
}

export function buildQrCodeReceipt(data: QRReceiptData): Uint8Array {
  const parts: Uint8Array[] = []

  parts.push(COMMANDS.INIT)
  parts.push(COMMANDS.ALIGN_CENTER)
  parts.push(COMMANDS.FONT_DOUBLE_HEIGHT)
  parts.push(COMMANDS.BOLD_ON)
  parts.push(encodeText(truncate(data.eventName || 'EventPay', PAPER_WIDTH)))
  parts.push(COMMANDS.NEW_LINE)
  parts.push(COMMANDS.BOLD_OFF)
  parts.push(COMMANDS.FONT_NORMAL)

  if (data.userName) {
    parts.push(encodeText(truncate(data.userName, PAPER_WIDTH)))
    parts.push(COMMANDS.NEW_LINE)
  }

  parts.push(COMMANDS.NEW_LINE)

  const qrData = encodeText(data.barcode)
  const storeLen = qrData.length + 3
  const pL = storeLen % 256
  const pH = Math.floor(storeLen / 256)

  // Select model 2
  parts.push(new Uint8Array([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00]))
  // Module size
  parts.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x06]))
  // Error correction level (M)
  parts.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31]))
  // Store data
  parts.push(new Uint8Array([GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30]))
  parts.push(qrData)
  // Print QR code
  parts.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]))

  parts.push(COMMANDS.NEW_LINE)
  parts.push(COMMANDS.NEW_LINE)
  parts.push(encodeText(truncate(data.barcode, PAPER_WIDTH)))
  parts.push(COMMANDS.NEW_LINE)
  parts.push(COMMANDS.FEED_LINES(4))
  parts.push(COMMANDS.CUT_PARTIAL)

  return concat(...parts)
}

export { COMMANDS, PAPER_WIDTH }
